import { supabase } from '../lib/supabase';

export interface ReferralConfig {
  id: string;
  key: string;
  value: any;
  label: string;
  description: string;
  updated_at: string;
  updated_by: string | null;
}

export interface ReferralTreeNode {
  id: string;
  referred_id: string;
  referred_name: string | null;
  referred_username: string | null;
  referred_avatar: string | null;
  depth: number;
  status: string;
  signup_tokens_awarded: boolean;
  subscription_tokens_awarded: boolean;
  completed_at: string;
  has_subscription: boolean;
}

export interface ReferralStats {
  direct_referrals: number;
  pending_referrals: number;
  total_tree_size: number;
  total_tokens_earned: number;
  subscribers_in_tree: number;
  max_direct_referrals: number;
  slots_remaining: number;
}

export class ReferralService {
  async getUserReferralCode(userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('referral_code')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data?.referral_code || null;
  }

  async getReferralStats(userId: string): Promise<ReferralStats> {
    const { data, error } = await supabase.rpc('get_referral_stats', {
      p_user_id: userId,
    });

    if (error) throw error;
    return data as ReferralStats;
  }

  async getReferralTree(userId: string): Promise<ReferralTreeNode[]> {
    const { data, error } = await supabase.rpc('get_referral_tree', {
      p_user_id: userId,
    });

    if (error) throw error;
    return (data as ReferralTreeNode[]) || [];
  }

  async processReferralSignup(
    referredUserId: string,
    referralCode: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase.rpc('process_referral_signup', {
      p_referred_user_id: referredUserId,
      p_referral_code: referralCode,
    });

    if (error) throw error;

    if (data?.error) {
      return { success: false, error: data.error };
    }

    return { success: true };
  }

  async getReferralConfig(): Promise<ReferralConfig[]> {
    const { data, error } = await supabase
      .from('referral_config')
      .select('*')
      .order('key');

    if (error) throw error;
    return data || [];
  }

  async updateReferralConfig(
    key: string,
    value: any,
    adminId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('referral_config')
      .update({
        value: JSON.stringify(value),
        updated_at: new Date().toISOString(),
        updated_by: adminId,
      })
      .eq('key', key);

    if (error) throw error;
  }

  async getRulesText(): Promise<string> {
    const { data, error } = await supabase
      .from('referral_config')
      .select('value')
      .eq('key', 'rules_text')
      .maybeSingle();

    if (error) throw error;
    return data?.value || '';
  }

  async isProgramEnabled(): Promise<boolean> {
    const { data, error } = await supabase
      .from('referral_config')
      .select('value')
      .eq('key', 'program_enabled')
      .maybeSingle();

    if (error) throw error;
    return data?.value === true || data?.value === 'true';
  }

  async getConfigValue(key: string): Promise<any> {
    const { data, error } = await supabase
      .from('referral_config')
      .select('value')
      .eq('key', key)
      .maybeSingle();

    if (error) throw error;
    return data?.value;
  }

  async getAllReferrals(filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: any[]; count: number }> {
    let query = supabase
      .from('referrals')
      .select(
        `
        *,
        referrer:referrer_id(id, full_name, username, avatar_url),
        referred:referred_id(id, full_name, username, avatar_url)
      `,
        { count: 'exact' }
      )
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.limit && filters?.offset !== undefined) {
      query = query.range(
        filters.offset,
        filters.offset + filters.limit - 1
      );
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], count: count || 0 };
  }

  async getAdminReferralStats(): Promise<{
    totalReferrals: number;
    completedReferrals: number;
    pendingReferrals: number;
    totalSignupTokensAwarded: number;
    totalSubscriptionBonusesAwarded: number;
    usersWithReferrals: number;
  }> {
    const { data: referrals, error } = await supabase
      .from('referrals')
      .select('status, signup_tokens_awarded, subscription_tokens_awarded, referrer_id');

    if (error) throw error;

    const completed = referrals?.filter((r) => r.status === 'completed') || [];
    const pending = referrals?.filter((r) => r.status === 'pending') || [];
    const uniqueReferrers = new Set(
      completed.map((r) => r.referrer_id)
    );

    const { data: signupTokens } = await supabase
      .from('coin_transactions')
      .select('amount')
      .like('description', 'Referral Gift%');

    const { data: subTokens } = await supabase
      .from('coin_transactions')
      .select('amount')
      .like('description', 'Referral Subscriber Bonus%');

    return {
      totalReferrals: referrals?.length || 0,
      completedReferrals: completed.length,
      pendingReferrals: pending.length,
      totalSignupTokensAwarded: signupTokens?.reduce(
        (sum, t) => sum + Number(t.amount),
        0
      ) || 0,
      totalSubscriptionBonusesAwarded: subTokens?.reduce(
        (sum, t) => sum + Number(t.amount),
        0
      ) || 0,
      usersWithReferrals: uniqueReferrers.size,
    };
  }
}
